import { Editor, Transforms, Descendant, Node } from 'slate';
import { ReactEditor } from 'slate-react';
import { createParagraph } from '../utils/paragraph';
import { htmlCarriesContentBeyondImages } from '../../PlateEditor/serializers/pasteNormalize';

/**
 * Maximum image file size allowed for paste (5MB).
 * Images exceeding this are silently ignored to prevent
 * bloated editor state and slow serialization.
 */
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * Allowed MIME types for pasted images.
 */
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];

/**
 * Plugin that enables image paste (Ctrl+V / Cmd+V) support in the Slate editor.
 *
 * When a user pastes clipboard content that contains image files (e.g. screenshots,
 * copied images), this plugin intercepts the paste, reads the image as a base64
 * data URL, and inserts it as a Slate image element.
 *
 * The base64 is kept in the editor until save, when processImagesBeforeSave()
 * uploads them to mnemosyne S3 and replaces with mnemosyne://docId references.
 *
 * Works across the legacy Slate editor's touchpoints: the DataTable
 * add-comment cell, the deal-application form note, the deal-logs note, and
 * the share-with-lender modal — all via TextAreaEditor.tsx. Compose Email,
 * One-Pager, Engagement Letter, and KYC & Bureau Letter are on PlateEditor,
 * not this plugin.
 */
function withImagePaste<T extends Editor>(editor: T): T {
    const { insertData } = editor;

    editor.insertData = (data: DataTransfer) => {
        const { files } = data;

        // HTML that carries real content (text, lists, tables, headings — see
        // htmlCarriesContentBeyondImages) wins over an image file riding on the
        // same clipboard, so a paste of real text next to a Word/browser bitmap
        // falls through to the wrapped insertData below instead of dropping the
        // text. An image-only clipboard (screenshots, copied images) still
        // intercepts here and inserts the image, same as before.
        if (files && files.length > 0 && !htmlCarriesContentBeyondImages(data.getData('text/html'))) {
            const imageFiles = Array.from(files).filter(
                (file) => ALLOWED_IMAGE_TYPES.includes(file.type) && file.size <= MAX_IMAGE_SIZE_BYTES,
            );

            if (imageFiles.length > 0) {
                for (const file of imageFiles) {
                    const reader = new FileReader();
                    reader.addEventListener('load', () => {
                        const url = reader.result as string;
                        if (url) {
                            insertImageNode(editor, url);
                        }
                    });
                    reader.readAsDataURL(file);
                }
                return; // Prevent default paste behavior for image files
            }
        }

        // Fall through to other paste plugins (withHtmlPaste, withExcelPaste, etc.)
        insertData(data);
    };

    return editor;
}

/**
 * Inserts an image element into the editor at the current selection,
 * followed by an empty paragraph for continued typing.
 * After insertion, loads the image to capture natural width so PDFs
 * and emails render at correct size instead of full width.
 */
function insertImageNode(editor: Editor, url: string) {
    const imageNode = {
        type: 'image' as const,
        url,
        alt: 'Pasted image',
        children: [{ text: '' }],
    };

    // Insert immediately (sync) so Slate state is consistent
    Transforms.insertNodes(editor, imageNode as unknown as Descendant);
    Transforms.insertNodes(editor, createParagraph('') as unknown as Descendant);

    // Then update width async after image loads — same pattern as resize in Image.jsx
    const img = new window.Image();
    img.onload = () => {
        try {
            // Find the inserted image node and update its width
            for (const [node, path] of Node.nodes(editor)) {
                if ((node as any).type === 'image' && (node as any).url === url && !(node as any).width) {
                    Transforms.setNodes(editor, { width: img.naturalWidth } as any, { at: path });
                    break;
                }
            }
        } catch (e) {
            // Editor may have changed — ignore
        }
    };
    img.src = url;
}

export default withImagePaste;
